import { useState, useMemo } from "react";

import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import CircularProgress from "@mui/material/CircularProgress";

import SoftButton from "components/SoftButton";
import SoftTagInput from "components/SoftTagInput";
import SoftInput from "components/SoftInput";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import InvitedUsersCard from "../InvitedUserCard";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { updateQuery } from "api/Auth0Wrapper";
import "./css/style.css";

function InviteModal({ openInviteModal, setOpenInviteModal, roles, reloadUsersList = Function(), users }) {
   const [emailAddresses, setEmailAddresses] = useState([]);
   const [tagInputError, setTagInputError] = useState(false);
   const { user } = useAuth0();
   const [userInstances, setUserInstances] = useState([]);
   const [userRoles, setUserRoles] = useState({});
   const [customMessage, setCustomMessage] = useState("");
   const [inviteUsersLimitReached, setInviteUsersLimitReached] = useState(false);
   const [emailsAlreadyInvited, setEmailsAlreadyInvited] = useState([]);
   const [emailAddressInputValue, setEmailAddressInputValue] = useState("");
   const [isLoadingInvite, setIsLoadingInvite] = useState(false);
   const MaxEmailAddressCharacterLength = 320;
   // const paymentgateway = "http://localhost:3001/";

   const handleClose = () => {
      setIsLoadingInvite(false);
      setOpenInviteModal(false);
      setEmailAddresses([]); // Clear the input tags when the modal is closed
      setCustomMessage(""); // Clear the input tags when the modal is closed
      setUserInstances([]);
      setTagInputError(false);
      setInviteUsersLimitReached(false);
      setEmailsAlreadyInvited([]);
   };
   const validateEmail = (email) => {
      const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return regex.test(email);
   };

   const checkIsEmailExist = (emails) => {
      const existingUsersEmails = users.map((user) => user.email_address);

      const exisitingEmailsInput = emails.filter((email) => {
         return existingUsersEmails.includes(email);
      });

      setEmailsAlreadyInvited(exisitingEmailsInput);
      return exisitingEmailsInput;
   };

   const handleTagsChange = (tags) => {
      const validEmails = tags.filter(validateEmail);
      const newInstances = validEmails.map((email) => ({ email }));

      setInviteUsersLimitReached(false);

      if (validEmails?.length > 10) {
         setInviteUsersLimitReached(true);
         return;
      }

      if (validEmails.length > 0) {
         checkIsEmailExist(validEmails);
      } else {
         setEmailsAlreadyInvited([]);
      }

      // setUserRoles({})
      setUserInstances(newInstances);
      setEmailAddresses(validEmails);
      setTagInputError(validEmails.length === 0);
   };

   const handleCustomMessageChange = (event) => {
      setCustomMessage(event.target.value);
   };

   const inviteUsers = async () => {
      setIsLoadingInvite(true);
      if (emailAddresses.length === 0) {
         setTagInputError(true);
         setIsLoadingInvite(false);
         return; // Stop the invite process if there are no valid email addresses
      }

      let data = JSON.stringify({
         query: `mutation InviteUser($tenantId: String!, $input: InviteInput) {
        inviteUser(tenantId: $tenantId, input: $input) {
          isSuccessful
          message
          resultObject
        }
      }`,
         variables: {
            tenantId: user?.stripeId,
            input: {
               emails: emailAddresses,
               inviter: user?.cxfUserId,
               messages: customMessage,
               roles: userRoles
            }
         }
      });
      try {
         const response = await updateQuery(data);

         if (response?.inviteUser?.isSuccessful && response?.inviteUser?.resultObject?.length > 0) {
            // Request was successful (status 200-299)
            toast.success(response.inviteUser.message, 2500);
         } else if (!response?.inviteUser?.isSuccessful) {
            toast.warning(response.inviteUser.message, 2500);
         } else {
            // Request encountered an error (status outside 200-299)
            toast.error(response.inviteUser.message, 2500);
         }
      } catch (error) {
         console.log(error);
      }

      setIsLoadingInvite(false);
      setOpenInviteModal(false);
      setEmailAddresses([]); // Clear the input tags after the invite is sent
      setCustomMessage(""); // Clear the input tags when the modal is closed
      setUserInstances([]);
      setTagInputError(false);
      setInviteUsersLimitReached(false);
      setUserRoles([]); // Clear the userRoles when the modal is closed
      setEmailsAlreadyInvited([]);
      reloadUsersList();
   };

   const handleEmailAddressInputValueChange = (e) => {
      if (e.length <= MaxEmailAddressCharacterLength) {
         setEmailAddressInputValue(e);
      }
   };

   const renderEmailsAlreadyInvited = useMemo(() => {
      return (
         <SoftBox>
            <SoftTypography variant="caption" color="warning" fontWeight="light">
               The following emails have already been invited:
            </SoftTypography>
            <SoftBox mt={0.5} ml={2}>
               <List>
                  {emailsAlreadyInvited.map((email) => (
                     <ListItem key={email}>
                        <SoftTypography key={email} variant="caption" color="warning" fontWeight="light">
                           - {email}
                        </SoftTypography>
                     </ListItem>
                  ))}
               </List>
            </SoftBox>
         </SoftBox>
      );
   }, [emailsAlreadyInvited]);

   return (
      <Dialog className="invite-modal" open={openInviteModal} onClose={handleClose} maxWidth="sm" fullWidth={true}>
         <DialogTitle>Invite a user</DialogTitle>
         <DialogContent>
            <SoftBox mt={1}>
               <SoftTagInput
                  value={emailAddresses}
                  inputValue={emailAddressInputValue}
                  onChange={handleTagsChange}
                  addOnBlur
                  validationRegex={/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/}
                  mb={2}
                  error={tagInputError || inviteUsersLimitReached}
                  onValidation={(hasError) => setTagInputError(hasError)}
                  onChangeInput={handleEmailAddressInputValueChange}
               />
               {tagInputError && (
                  <SoftTypography variant="caption" color="error" fontWeight="light" mt={1}>
                     Please enter at least one valid email address. <br />
                  </SoftTypography>
               )}
               {inviteUsersLimitReached && (
                  <SoftTypography variant="caption" color="warning" fontWeight="light" mt={1}>
                     You can only invite up to 10 users at a time
                     <br />
                  </SoftTypography>
               )}
               {emailsAlreadyInvited.length > 0 && renderEmailsAlreadyInvited}
            </SoftBox>
            {userInstances.length > 0 ? <Divider variant="light" /> : null}
            <SoftBox>
               <InvitedUsersCard users={userInstances} userRoles={userRoles} roles={roles} setUserRoles={setUserRoles} />
            </SoftBox>
            {userInstances.length > 0 ? <Divider variant="light" /> : null}

            <SoftBox>
               <SoftTypography variant="h6" fontWeight="medium">
                  Type a custom message
               </SoftTypography>
               <SoftInput placeholder="Hey! I'm inviting you to my team" value={customMessage} onChange={handleCustomMessageChange} />
            </SoftBox>
         </DialogContent>
         <DialogActions>
            <SoftButton variant="outlined" color="info" onClick={handleClose}>
               cancel
            </SoftButton>
            <SoftBox position="relative" marginLeft={1}>
               <SoftButton variant="contained" color="info" onClick={inviteUsers} disabled={tagInputError || emailsAlreadyInvited.length > 0 || emailAddresses.length === 0 || isLoadingInvite}>
                  send invite
               </SoftButton>
               {isLoadingInvite && (
                  <CircularProgress
                     size={24}
                     sx={{
                        color: "#fff",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                        zIndex: 1
                     }}
                  />
               )}
            </SoftBox>
         </DialogActions>
      </Dialog>
   );
}

InviteModal.defaultProp = {
   openInviteModal: false
};

InviteModal.propTypes = {
   openInviteModal: PropTypes.bool,
   setOpenInviteModal: PropTypes.func
};
export default InviteModal;
